import { memo, type ReactNode, useMemo } from 'react';

import { ChevronDownIcon as TriangleDownIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';

import { DropdownMenuWithSelectedLabelButton } from '~/components/Menus/DropdownMenuWithSelectedLabelButton';

type CollectionFormFieldProps = {
  fieldName: string;
  leftAdornment?: ReactNode;
  onSelect: (isRequired: boolean) => Promise<void>;
  required: boolean;
  ellipsisMenuOptions?: DropdownMenuOption[];
  disabled?: boolean;
};

export const CollectionFormField = memo(
  ({ fieldName, leftAdornment, required, ellipsisMenuOptions, onSelect, disabled }: CollectionFormFieldProps) => {
    const options = useMemo(() => {
      const optionsArray: DropdownMenuOption[] = [
        {
          id: 'optional',
          label: 'Optional',
          onSelect: () => onSelect(false),
          type: 'item',
        },
        {
          id: 'required',
          label: 'Required',
          onSelect: () => onSelect(true),
          type: 'item',
        },
      ];

      if (ellipsisMenuOptions?.length) {
        optionsArray.push({
          id: 'separator',
          type: 'separator',
        });

        optionsArray.push(...ellipsisMenuOptions);
      }

      return optionsArray;
    }, [ellipsisMenuOptions, onSelect]);

    return (
      <div className="flex h-12 justify-between gap-4 rounded-4 border ads:border-ads-gray-5 border-gray-5 px-3">
        <div
          className="flex items-center gap-2 ads:text-secondary text-14 text-gray-11"
          data-testid="CONTENT_COLLECTION_FORM_MODAL_FIELD_NAME"
          data-name={fieldName}
        >
          <div className="shrink-0">{leftAdornment || <div className="size-4" />}</div>
          <div className="truncate">{fieldName}</div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <DropdownMenuWithSelectedLabelButton
            selectedOption={required ? { id: 'required', label: 'Required' } : { id: 'optional', label: 'Optional' }}
            options={options}
            trigger={
              <Button
                appearance="filled"
                className="min-w-[106px]"
                color="gray"
                size="medium"
                data-testid="CONTENT_COLLECTION_FORM_MODAL_FIELD_REQUIREMENT_BUTTON"
                data-name={fieldName}
                suffix={<TriangleDownIcon size="sm" />}
              >
                {required ? 'Required' : 'Optional'}
              </Button>
            }
            disabled={disabled}
          />
        </div>
      </div>
    );
  },
);

CollectionFormField.displayName = 'CollectionFormField';
